import { redactHuddleInvitationValues } from '@naumu/shared/huddles/redact-url';
/**
 * PostHog client initialization for frontend
 *
 * PostHog is an essential service used for feature flags,
 * customer support, and product analytics.
 */

import type { PostHog } from 'posthog-js';

import packageJson from '../../../../package.json';
import { isCredentialRoutePath } from '../analytics/credential-routes';

import { isDesktopShell, isNativeShell } from '@naumu/shared/native/is-native-shell';

import { dropIgnoredExceptionEvents } from './ignore-client-errors';

let initialized = false;
let devMode = false;

/**
 * The live posthog-js instance, or null until the module has been fetched.
 *
 * posthog-js is ~190 KB of parsed JS and it used to be a STATIC import here,
 * so every route - marketing pages included - paid for it in the initial
 * script set before anything could hydrate. It is loaded through a dynamic
 * import instead, kicked off after the page goes idle, and every call made in
 * the meantime is queued below and replayed in order once it lands. Nothing
 * about the public surface (`track` / `identify` / `reset` / consent) changes.
 */
let instance: PostHog | null = null;
let loading: Promise<void> | null = null;

/** Calls made before posthog-js finished loading, replayed in order on arrival. */
const pending: Array<(ph: PostHog) => void> = [];
/** Hard cap so a page that never reaches init can't grow the queue unbounded. */
const MAX_PENDING = 100;

const flushPending = () => {
	if (!instance) return;
	while (pending.length > 0) {
		const run = pending.shift();
		try {
			run?.(instance);
		} catch (error) {
			console.error('[PostHog] queued call failed:', error);
		}
	}
};

/**
 * Run `fn` against the live PostHog instance, queueing it if the library is
 * still in flight. Dropped entirely in dev mode (no instance will ever exist).
 *
 * Every posthog-js touchpoint outside this module goes through here, so
 * analytics dispatch is unchanged from a caller's point of view - a capture
 * issued during the load window is delivered, not lost.
 */
export const withPostHog = (fn: (ph: PostHog) => void): void => {
	if (instance) {
		fn(instance);
		return;
	}
	if (devMode) return;
	if (pending.length >= MAX_PENDING) return;
	pending.push(fn);
};

type AnalyticsPlatform = 'desktop' | 'mobile' | 'web';

interface VersionSuperProperties {
	app_version: string;
	platform: AnalyticsPlatform;
	desktop_version?: string;
}

/**
 * Super properties attached to every captured event: which web build the user
 * is on, which shell it renders in, and (on desktop) the Electron app version.
 *
 * Read at init time — both shell globals are injected before page scripts run
 * (`window.__NAUMU_NATIVE__` / `window.naumuDesktop` via contextBridge), so the
 * values are already present. Still fully guarded for SSR and for a desktop
 * bridge that predates `appVersion`.
 */
const buildVersionSuperProperties = (): VersionSuperProperties => {
	const isDesktop = isDesktopShell();
	const platform: AnalyticsPlatform = isDesktop
		? 'desktop'
		: isNativeShell()
			? 'mobile'
			: 'web';

	const desktopVersion =
		isDesktop && typeof window !== 'undefined'
			? window.naumuDesktop?.appVersion
			: undefined;

	return {
		app_version: packageJson.version,
		platform,
		...(desktopVersion ? { desktop_version: desktopVersion } : {}),
	};
};

export const initPostHog = () => {
	if (initialized) return;

	// Check for dev mode tracking (via env or localStorage)
	const enableDevTracking = typeof window !== 'undefined' &&
		(process.env.NEXT_PUBLIC_POSTHOG_DEV === 'true' ||
		 localStorage.getItem('posthog-dev-tracking') === 'true');

	// Only ship events from the production deployment — staging runs with
	// NODE_ENV=production but NEXT_PUBLIC_DEPLOYMENT=staging, so gate on that.
	if (process.env.NEXT_PUBLIC_DEPLOYMENT !== 'production' && !enableDevTracking) {
		console.info('[PostHog] Not initialized: deployment is not production. Set localStorage posthog-dev-tracking=true to enable.');
		devMode = true; // Enable console logging even without PostHog
		initialized = true;
		// No instance will ever exist here, so anything queued before init can
		// never be replayed - drop it rather than pin it in memory for the
		// lifetime of the page. withPostHog() stops queueing from now on.
		pending.length = 0;
		return;
	}

	if (typeof window !== 'undefined') {
		const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
		if (apiKey) {
			// Marked initialized synchronously so a second call can't start a
			// second import; the actual instance arrives a tick later and
			// flushPending() replays whatever was captured in between.
			initialized = true;
			loading = import('posthog-js').then(({ default: posthog }) => {
				instance = posthog;
				bootPostHog(posthog, apiKey);
				flushPending();
			}).catch((error) => {
				console.error('[PostHog] failed to load:', error);
				// The import will never resolve, so the queue is unreplayable.
				// Drop it (and say so once) instead of stranding the calls - and
				// flip to devMode so withPostHog() stops filling it again.
				if (pending.length > 0) {
					console.warn(`[PostHog] dropped ${pending.length} queued call(s) after load failure`);
					pending.length = 0;
				}
				devMode = true;
			});
		} else {
			console.info('[PostHog] No API key configured');
			devMode = true;
			initialized = true;
			pending.length = 0;
		}
	}
};

/** The real posthog-js bootstrap, run once the library has been fetched. */
const bootPostHog = (posthog: PostHog, apiKey: string) => {
	posthog.init(apiKey, {
		api_host: '/_x',
		ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
		loaded: (posthog) => {
			if (process.env.NODE_ENV === 'development') posthog.debug();
		},
		// Capture pageviews manually via PostHogPageviewTracker (App Router)
		capture_pageview: false,
		// Guest links are bearer credentials; no replay records their URL or traffic.
		disable_session_recording: isCredentialRoutePath(window.location.pathname),
		session_recording: { maskCapturedNetworkRequestFn: (request) => redactHuddleInvitationValues(request) },
		// Persist user identity across sessions
		persistence: 'localStorage+cookie',
		// GDPR consent gate: start opted OUT so capture() is a NO-OP until the
		// user explicitly grants analytics consent (see lib/consent/analytics-
		// consent). Feature flags still load while opted out - this only gates
		// event/pageview CAPTURE, not init. applyAnalyticsConsent() flips this.
		opt_out_capturing_by_default: true,
		// PostHog Error Tracking (we use PostHog rather than Sentry). Autocaptures
		// unhandled errors and unhandled promise rejections as `$exception` events.
		// Console errors stay OFF - they are noise, not crashes.
		//
		// This runs for EVERY surface, because init() is called once from
		// MinimalProviders, which wraps the whole app: marketing and public
		// visitor pages, the authenticated workspace, and the Electron desktop
		// renderer (which loads this same web build). Errors React swallows into
		// an error boundary never reach window.onerror, so those are reported by
		// hand through ./error-reporting.
		//
		// Exceptions capture through the normal capture() path, so the consent
		// gate above covers them with no extra check, and the disabled/dev path
		// stays silent (no instance is ever created there).
		capture_exceptions: {
			capture_unhandled_errors: true,
			capture_unhandled_rejections: true,
			capture_console_errors: false,
		},
		// Drops the expected-noise exceptions (aborted fetches, ResizeObserver
		// loop warnings, browser-extension frames, offline network failures,
		// failed analytics requests, and every post-reload ChunkLoadError) before
		// they leave the browser. Non-exception events pass through untouched.
		// Same predicate as reportClientError.
		before_send: (event) => redactHuddleInvitationValues(dropIgnoredExceptionEvents(event)),
	});

	// Patch PostHog's external script loader to strip "posthog-" prefix
	// from script names. Ad blockers match on "posthog" in URL paths,
	// so we serve these scripts under clean names via Next.js rewrites.
	const ext = (window as Record<string, any>).__PosthogExtensions__;
	if (ext?.loadExternalDependency) {
		const orig = ext.loadExternalDependency;
		ext.loadExternalDependency = (ph: unknown, kind: string, cb: unknown) => {
			orig(ph, kind.replace(/^posthog-/, ''), cb);
		};
	}

	// Version/platform context on every event captured from here on.
	posthog.register(buildVersionSuperProperties());

	// We init opted OUT by default (GDPR). If the user already granted
	// analytics consent in a prior session, opt back in now so capture
	// resumes. We read the persisted decision directly (rather than
	// importing the consent module) to avoid an import cycle -
	// analytics-consent.ts imports applyAnalyticsConsent from here.
	if (readPersistedAnalyticsConsent() === 'granted') {
		posthog.opt_in_capturing();
	}
};

/**
 * Check if PostHog is initialized and ready
 */
export const isPostHogReady = (): boolean => {
	return instance !== null && typeof window !== 'undefined';
};

/** Resolves once the deferred posthog-js import has settled (tests/diagnostics). */
export const whenPostHogLoaded = (): Promise<void> => loading ?? Promise.resolve();

/**
 * Check if running in dev mode (console logging only)
 */
export const isDevMode = (): boolean => {
	return devMode;
};

/**
 * Read the persisted analytics-consent decision straight from localStorage.
 *
 * Kept here (duplicating the storage key from lib/consent/analytics-consent.ts)
 * purely to break an import cycle: the consent module imports
 * `applyAnalyticsConsent` from this file, so this file cannot import it back.
 * The consent module remains the public read/write API for feature code.
 */
const ANALYTICS_CONSENT_STORAGE_KEY = 'naumu:analytics-consent';

const readPersistedAnalyticsConsent = (): 'granted' | 'denied' | null => {
	if (typeof window === 'undefined') return null;
	try {
		const stored = localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
		if (stored === 'granted') return 'granted';
		if (stored === 'denied') return 'denied';
		return null;
	} catch {
		return null;
	}
};

/**
 * Apply an analytics-consent decision to the live PostHog instance.
 *
 * `granted` → opt back IN to event/pageview capture.
 * `false`   → opt OUT (capture becomes a no-op again).
 *
 * No-op when PostHog isn't actually initialized (dev mode, no API key, SSR) —
 * there's nothing to toggle, and the opt-out-by-default + persisted-consent
 * read in initPostHog() handles the next real init.
 *
 * Feature code must never call posthog directly; the consent module
 * (lib/consent/analytics-consent.ts) calls this on every decision.
 */
export const applyAnalyticsConsent = (granted: boolean): void => {
	if (isDevMode()) return;
	// Queued when the library is still in flight, so a consent decision made
	// during boot is applied the moment posthog-js lands rather than dropped.
	withPostHog((posthog) => {
		if (granted) {
			posthog.opt_in_capturing();
			// A credential-route hold left in place by an opted-out exit: normal policy resumes now (ordinary routes only).
			releaseCredentialReplayHold(posthog);
		} else {
			posthog.opt_out_capturing();
		}
	});
};

// `disable_session_recording: true` is currently held in this document
// because of a credential route (boot-time check or the guard). Released
// exactly once, on an ordinary route, by the guard or by a later consent
// grant (an opted-out exit keeps the hold, since nothing may start then).
let credentialReplayHold = typeof window !== 'undefined' && isCredentialRoutePath(window.location.pathname);
// The document is currently on a credential route (guard state).
let onCredentialRoute = credentialReplayHold;

/** Hand replay back to the normal policy: no start call, no sampling override; consent must already be granted. */
const releaseCredentialReplayHold = (posthog: PostHog): void => {
	if (!credentialReplayHold || onCredentialRoute || posthog.has_opted_out_capturing()) return;
	credentialReplayHold = false;
	posthog.set_config({ disable_session_recording: false });
};

/**
 * Route-aware replay switch for the SPA: the boot-time
 * `disable_session_recording` only covers a fresh document load. A replay
 * started on `/spaces` would keep recording across an in-app navigation into a
 * credential route, so the web app calls this on every pathname change.
 *
 * Entering a credential route sets `disable_session_recording: true` through
 * `set_config` for the whole route: posthog-js stops an active recorder on
 * that change AND a recorder whose remote config or dependency is still
 * loading never starts under the previous config (no timing assumption on
 * `sessionRecordingStarted()`). Leaving restores the normal policy exactly
 * once, `set_config({ disable_session_recording: false })`, with no start
 * call and no sampling / linked-flag override: the project setting, sampling
 * and consent decide as they would on a fresh `/spaces` load. Leaving while
 * opted out keeps the hold; `applyAnalyticsConsent(true)` releases it later
 * on an ordinary route, never on a credential one. Ordinary route changes
 * without a hold touch nothing. Queued while posthog-js is in flight; a
 * no-op in dev mode.
 */
export const setCredentialRouteReplay = (onRoute: boolean): void => {
	if (isDevMode()) return;
	withPostHog((posthog) => {
		onCredentialRoute = onRoute;
		if (onRoute) {
			credentialReplayHold = true;
			posthog.set_config({ disable_session_recording: true });
			return;
		}
		releaseCredentialReplayHold(posthog);
	});
};
